
import React, { useState, useRef, useEffect } from 'react';
import { ShoppingCart, Layout, Sparkles, User, Search, Flower, Globe, LogOut, ClipboardList, Palette, MessageCircle, Settings, HelpCircle, ChevronDown, Coins } from 'lucide-react';
import { Language, Currency, User as UserType } from '../types';

interface NavbarProps {
  cartCount: number;
  onCartClick: () => void;
  onChatClick: () => void;
  onAdminClick: () => void;
  onNavigate: (page: string) => void;
  onUserAuthClick: () => void;
  currentUser: UserType | null;
  onLogout: () => void;
  activePage: string;
  language: Language;
  currency: Currency;
  onChangeLanguage: (lang: Language) => void;
  onChangeCurrency: (curr: Currency) => void;
}

// Flag component with SVG vectors for PT (Brazil/Portugal), EN (USA/UK), ES (Spain), RU (Russia)
const FlagIcon: React.FC<{ code: Language; className?: string }> = ({ code, className = "w-4 h-3" }) => {
  switch (code) {
    case 'pt':
      // Brazilian Flag (or PT)
      return (
        <svg viewBox="0 0 720 504" className={`${className} rounded-[1px] shadow-sm inline-block flex-shrink-0`} xmlns="http://www.w3.org/2000/svg">
          <rect width="720" height="504" fill="#009b3a"/>
          <polygon points="360,42 678,252 360,462 42,252" fill="#fedf00"/>
          <circle cx="360" cy="252" r="105" fill="#002776"/>
          <path d="M260,255 C280,215 380,215 460,270" stroke="#ffffff" strokeWidth="14" fill="none"/>
        </svg>
      );
    case 'en':
      // US / English Flag
      return (
        <svg viewBox="0 0 741 390" className={`${className} rounded-[1px] shadow-sm inline-block flex-shrink-0`} xmlns="http://www.w3.org/2000/svg">
          <rect width="741" height="390" fill="#b22234"/>
          <path d="M0,30 H741 M0,90 H741 M0,150 H741 M0,210 H741 M0,270 H741 M0,330 H741" stroke="#ffffff" strokeWidth="30"/>
          <rect width="296" height="210" fill="#3c3b6e"/>
          <circle cx="148" cy="105" r="50" fill="#ffffff" opacity="0.9"/>
        </svg>
      );
    case 'es':
      // Spanish Flag
      return (
        <svg viewBox="0 0 750 500" className={`${className} rounded-[1px] shadow-sm inline-block flex-shrink-0`} xmlns="http://www.w3.org/2000/svg">
          <rect width="750" height="500" fill="#aa151b"/>
          <rect y="125" width="750" height="250" fill="#f1bf00"/>
        </svg>
      );
    case 'ru':
      // Russian Flag
      return (
        <svg viewBox="0 0 900 600" className={`${className} rounded-[1px] shadow-sm inline-block flex-shrink-0`} xmlns="http://www.w3.org/2000/svg">
          <rect width="900" height="600" fill="#d52b1e"/>
          <rect width="900" height="400" fill="#0039a6"/>
          <rect width="900" height="200" fill="#ffffff"/>
        </svg>
      );
    default:
      return null;
  }
};

const Navbar: React.FC<NavbarProps> = ({ 
  cartCount, 
  onCartClick, 
  onChatClick, 
  onAdminClick,
  onNavigate, 
  onUserAuthClick,
  currentUser,
  onLogout,
  activePage, 
  language, 
  currency,
  onChangeLanguage,
  onChangeCurrency
}) => {
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);
  const [isCurrencyDropdownOpen, setIsCurrencyDropdownOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);
  const currRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setIsLangDropdownOpen(false);
      }
      if (currRef.current && !currRef.current.contains(e.target as Node)) {
        setIsCurrencyDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const languagesList: { code: Language; label: string; native: string }[] = [
    { code: 'pt', label: 'Português', native: 'PT' },
    { code: 'en', label: 'English', native: 'EN' },
    { code: 'es', label: 'Español', native: 'ES' },
    { code: 'ru', label: 'Русский', native: 'RU' }
  ];

  const currenciesList: { code: Currency; symbol: string; label: string }[] = [
    { code: 'USD', symbol: '$', label: 'USD ($)' },
    { code: 'BRL', symbol: 'R$', label: 'BRL (R$)' },
    { code: 'EUR', symbol: '€', label: 'EUR (€)' },
    { code: 'RUB', symbol: '₽', label: 'RUB (₽)' }
  ];

  const t = {
    pt: { home: 'Início', gallery: 'Galeria', custom: 'Arte Custom', faq: 'F.A.Q', elite: 'Conta', logout: 'Sair', myOrders: 'Meus Pedidos', chat: 'Chat' },
    en: { home: 'Home', gallery: 'Gallery', custom: 'Custom Art', faq: 'F.A.Q', elite: 'Account', logout: 'Logout', myOrders: 'My Orders', chat: 'Chat' },
    es: { home: 'Inicio', gallery: 'Galería', custom: 'Arte Personalizado', faq: 'F.A.Q', elite: 'Cuenta', logout: 'Cerrar sesión', myOrders: 'Mis Pedidos', chat: 'Chat' },
    ru: { home: 'Главная', gallery: 'Галерея', custom: 'Заказной Арт', faq: 'F.A.Q', elite: 'Аккаунт', logout: 'Выйти', myOrders: 'Мои Заказы', chat: 'Чат' }
  }[language] || { home: 'Home', gallery: 'Gallery', custom: 'Custom Art', faq: 'F.A.Q', elite: 'Account', logout: 'Logout', myOrders: 'My Orders', chat: 'Chat' };

  const currentLangObj = languagesList.find(l => l.code === language) || languagesList[1];
  const currentCurrObj = currenciesList.find(c => c.code === currency) || currenciesList[0];

  return (
    <nav className="sticky top-0 z-50 bg-black/95 backdrop-blur-lg border-b-2 border-[#8b5cf6]/50 px-4 md:px-8 py-3 md:py-4 overflow-visible shadow-[0_4px_30px_rgba(139,92,246,0.15)]">
      <div className="max-w-[1600px] mx-auto flex items-center justify-between overflow-visible">
        <div 
          className="flex items-center gap-3 cursor-pointer group whitespace-nowrap overflow-visible flex-shrink-0" 
          onClick={() => onNavigate('home')}
        >
          <div className="bg-white p-2 group-hover:bg-[#a855f7] transition-all shadow-[0_0_15px_rgba(168,85,247,0.3)] group-hover:shadow-[0_0_25px_rgba(168,85,247,0.6)] overflow-visible flex-shrink-0 flex items-center justify-center">
            <Flower className="w-4 h-4 text-black group-hover:text-white transition-transform group-hover:rotate-12" />
          </div>
          <span className="text-base md:text-lg font-black tracking-tighter text-white uppercase font-sans hidden sm:block pr-6 overflow-visible">
            NIIKO<span className="text-[#c084fc] font-extrabold tracking-widest ml-1 drop-shadow-[0_0_8px_rgba(192,132,252,0.6)]"> ARTWORK</span>
          </span>
        </div>

        <div className="hidden lg:flex items-center gap-10">
          {[
            { id: 'home', label: t.home, icon: Layout },
            { id: 'gallery', label: t.gallery, icon: Search },
            { id: 'custom-art', label: t.custom, icon: Palette },
            { id: 'faq', label: t.faq, icon: HelpCircle },
          ].map((link) => (
            <button
              key={link.id}
              onClick={() => onNavigate(link.id)}
              className={`flex items-center gap-3 text-[10px] font-black uppercase tracking-[0.4em] transition-all hover:text-[#a855f7] ${
                activePage === link.id ? 'text-[#c084fc] drop-shadow-[0_0_8px_rgba(192,132,252,0.6)] border-b border-[#a855f7] pb-1 -mb-1' : 'text-zinc-400 hover:text-zinc-200'
              }`}
            >
              <link.icon className="w-4 h-4" />
              {link.label}
            </button>
          ))}
          
          {currentUser && (
            <button
              onClick={() => onNavigate('my-orders')}
              className={`flex items-center gap-3 text-[10px] font-black uppercase tracking-[0.4em] transition-all hover:text-[#a855f7] ${
                activePage === 'my-orders' ? 'text-[#c084fc] drop-shadow-[0_0_8px_rgba(192,132,252,0.6)] border-b border-[#a855f7] pb-1 -mb-1' : 'text-zinc-400 hover:text-zinc-200'
              }`}
            >
              <ClipboardList className="w-4 h-4" />
              {t.myOrders}
            </button>
          )}
        </div>

        <div className="flex items-center gap-2.5 sm:gap-4 md:gap-5">
          {/* Language Switcher with Flag Icon */}
          <div className="relative" ref={langRef}>
            <button
              onClick={() => {
                setIsLangDropdownOpen(!isLangDropdownOpen);
                setIsCurrencyDropdownOpen(false);
              }}
              className="flex items-center gap-2 px-2.5 py-1.5 bg-black/60 hover:bg-purple-950/30 border border-purple-900/40 hover:border-purple-500/60 transition-all text-white text-[10px] font-black tracking-wider uppercase shadow-[0_0_10px_rgba(168,85,247,0.1)] focus:outline-none"
              title="Change Language / Alterar Idioma"
            >
              <FlagIcon code={language} className="w-4 h-3" />
              <span className="font-mono text-purple-200">{currentLangObj.native}</span>
              <ChevronDown className={`w-3 h-3 text-purple-400 transition-transform duration-200 ${isLangDropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {isLangDropdownOpen && (
              <div className="absolute right-0 mt-2 w-36 bg-zinc-950/95 backdrop-blur-xl border border-purple-800/50 shadow-[0_10px_30px_rgba(0,0,0,0.9),0_0_20px_rgba(168,85,247,0.2)] py-1 z-[100] animate-in fade-in slide-in-from-top-2 duration-200">
                {languagesList.map((langItem) => {
                  const isSelected = langItem.code === language;
                  return (
                    <button
                      key={langItem.code}
                      onClick={() => {
                        onChangeLanguage(langItem.code);
                        setIsLangDropdownOpen(false);
                      }}
                      className={`w-full flex items-center justify-between px-3 py-2 text-[11px] font-bold transition-all ${
                        isSelected 
                          ? 'bg-purple-900/40 text-purple-200 border-l-2 border-[#c084fc]' 
                          : 'text-zinc-400 hover:text-white hover:bg-white/5'
                      }`}
                    >
                      <div className="flex items-center gap-2.5">
                        <FlagIcon code={langItem.code} className="w-4 h-3" />
                        <span>{langItem.label}</span>
                      </div>
                      <span className="text-[9px] font-mono text-purple-400/80">{langItem.native}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Currency Switcher */}
          <div className="relative" ref={currRef}>
            <button
              onClick={() => {
                setIsCurrencyDropdownOpen(!isCurrencyDropdownOpen);
                setIsLangDropdownOpen(false);
              }}
              className="flex items-center gap-1.5 px-2.5 py-1.5 bg-black/60 hover:bg-purple-950/30 border border-purple-900/40 hover:border-purple-500/60 transition-all text-white text-[10px] font-black tracking-wider uppercase shadow-[0_0_10px_rgba(168,85,247,0.1)] focus:outline-none"
              title="Change Currency / Alterar Moeda"
            >
              <span className="text-purple-400 font-bold font-mono">{currentCurrObj.symbol}</span>
              <span className="font-mono text-purple-200">{currentCurrObj.code}</span>
              <ChevronDown className={`w-3 h-3 text-purple-400 transition-transform duration-200 ${isCurrencyDropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {isCurrencyDropdownOpen && (
              <div className="absolute right-0 mt-2 w-32 bg-zinc-950/95 backdrop-blur-xl border border-purple-800/50 shadow-[0_10px_30px_rgba(0,0,0,0.9),0_0_20px_rgba(168,85,247,0.2)] py-1 z-[100] animate-in fade-in slide-in-from-top-2 duration-200">
                {currenciesList.map((currItem) => {
                  const isSelected = currItem.code === currency;
                  return (
                    <button
                      key={currItem.code}
                      onClick={() => {
                        onChangeCurrency(currItem.code);
                        setIsCurrencyDropdownOpen(false);
                      }}
                      className={`w-full flex items-center justify-between px-3 py-2 text-[11px] font-bold transition-all ${
                        isSelected 
                          ? 'bg-purple-900/40 text-purple-200 border-l-2 border-[#c084fc]' 
                          : 'text-zinc-400 hover:text-white hover:bg-white/5'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-purple-400 font-mono font-bold w-4 text-center">{currItem.symbol}</span>
                        <span>{currItem.code}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <button 
            onClick={onChatClick}
            className="p-2.5 bg-black/30 border border-zinc-800 text-zinc-400 hover:text-[#a855f7] hover:border-[#a855f7]/50 hover:bg-[#a855f7]/10 transition-all"
          >
            <MessageCircle className="w-5 h-5" />
          </button>

          <button 
            onClick={onCartClick}
            className="relative p-2.5 bg-black/30 border border-zinc-800 text-zinc-400 hover:text-[#a855f7] hover:border-[#a855f7]/50 hover:bg-[#a855f7]/10 transition-all"
          >
            <ShoppingCart className="w-5 h-5" />
            {cartCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-[#a855f7] text-white text-[8px] font-black px-1.5 py-0.5 shadow-[0_0_10px_#a855f7]">
                {cartCount}
              </span>
            )}
          </button>
          
          {currentUser ? (
            <button 
              onClick={() => onNavigate('profile')}
              className={`flex items-center gap-3 px-4 py-2 border transition-all ${
                activePage === 'profile' ? 'bg-[#a855f7] border-[#a855f7] shadow-[0_0_10px_rgba(168,85,247,0.5)]' : 'bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 hover:border-[#a855f7]/50 text-zinc-300 hover:text-white'
              }`}
            >
              <div className="w-6 h-6 rounded-full overflow-hidden bg-black border border-white/10 flex-shrink-0">
                {currentUser.avatarUrl ? (
                  <img src={currentUser.avatarUrl} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <User className="w-3 h-3 text-zinc-500" />
                  </div>
                )}
              </div>
              <span className="text-[8px] font-black text-white uppercase tracking-tight truncate max-w-[80px]">{currentUser.name}</span>
            </button>
          ) : (
            <button 
              onClick={onUserAuthClick}
              className="hidden sm:flex items-center gap-3 bg-white text-black hover:bg-[#a855f7] hover:text-white px-6 py-3 text-[10px] font-black uppercase tracking-[0.2em] transition-all"
            >
              <User className="w-4 h-4" /> {t.elite}
            </button>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
